/**
 * Boot timing — measures where cold-start seconds actually go.
 *
 * Founder report 2026-06-13: 7s cold start. We bounded the worst-case
 * /api/users/me retry stall (1.1.189), but we were guessing at the
 * breakdown. This instruments the JS-side boot path so we stop guessing:
 * each milestone records ms-since-WebView-document-start
 * (performance.now() is relative to performance.timeOrigin), and once
 * the app is interactive we fire ONE PostHog `boot_timing` event with
 * the deltas.
 *
 * What this DOES capture: bundle parse/execute → React mount → auth
 * session restore → user-row fetch → first interactive paint.
 *
 * What it does NOT capture: the native splash + Capgo bundle
 * download/apply, which happen BEFORE any of our JS runs. If the
 * PostHog totals come back small (say ~2s) but the user still feels 7s,
 * the missing time is native/Capgo — a capacitor.config + update-policy
 * conversation, not a JS fix. That distinction is exactly what we need.
 *
 * Marks are sent both as individual deltas and as gaps between
 * consecutive milestones so the slow segment is obvious in PostHog.
 */
import { capturePostHog } from "./posthog";

type Mark = { name: string; at: number };

const _marks: Mark[] = [];
let _flushed = false;

/** Record a boot milestone. `at` is ms since the WebView document
 *  started (performance.now()), so marks are directly comparable. */
export function markBoot(name: string): void {
  try {
    _marks.push({ name, at: Math.round(performance.now()) });
  } catch {
    /* performance unavailable — skip silently */
  }
}

/** Fire the single boot_timing event. One-shot: subsequent calls no-op.
 *  Call when the app first becomes interactive (auth gate clears). */
export function flushBootTiming(): void {
  if (_flushed) return;
  _flushed = true;
  try {
    if (_marks.length === 0) return;
    const props: Record<string, unknown> = {};
    // Absolute ms-since-document-start for each milestone.
    for (const m of _marks) props[`at_${m.name}`] = m.at;
    // Gap between consecutive milestones — the slow segment stands out.
    for (let i = 1; i < _marks.length; i++) {
      const prev = _marks[i - 1];
      const cur = _marks[i];
      props[`gap_${prev.name}_to_${cur.name}`] = cur.at - prev.at;
    }
    props.total_ms = _marks[_marks.length - 1].at - _marks[0].at;
    props.platform = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } })
      .Capacitor?.isNativePlatform?.() ? "native" : "web";
    capturePostHog("boot_timing", props);
  } catch {
    /* never let instrumentation break boot */
  }
}
